import {
  Button,
  Flex,
  Heading,
  Icon,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooterButtons,
  useDisclosure,
  Modal,
  Textarea,
  Menu,
  MenuItemProps,
  Tag,
  Switch,
  Box,
  useToast,
} from '@televet/kibble-ui';
import { useTracking } from 'shared/providers/TrackingProvider';
import format from 'date-fns/format';
import { sortBy } from 'lodash-es';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SettingsSmsEvents } from 'shared/providers/TrackingProvider/EventNames';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  ClinicBlockedPhoneNumberUpdateInput,
  useFindManyClinicBlockedPhoneNumberQuery,
  useUpdateOneClinicBlockedPhoneNumberMutation,
} from 'shared/types/graphql';
import { formatPhoneNumber } from 'shared/utils';
import BlockPhoneNumberModal from './BlockPhoneNumberModal';

const BlockedPhoneNumbers = (): JSX.Element => {
  const { isOpen: isBlockNumberOpen, onOpen: onBlockNumberOpen, onClose: onBlockNumberClose } = useDisclosure();
  const { isOpen: isViewNotesOpen, onOpen: onViewNotesOpen, onClose: onViewNotesClose } = useDisclosure();
  const {
    isOpen: isEditBlockedNumberOpen,
    onOpen: onEditBlockedNumberOpen,
    onClose: onEditBlockedNumberClose,
  } = useDisclosure();
  const { clinicUser, currentClinicId: clinicId, hasAdminRoleAtCurrentClinic } = useClinicUser();
  const { trackEvent } = useTracking();

  const toast = useToast();

  const [blockedNumberNotes, setBlockedNumberNotes] = useState({
    number: '',
    notes: '',
    blockedBy: '',
    blockedAt: '',
    unblockedBy: '',
    unblockedAt: '',
  });

  const [editPhoneNumber, setEditPhoneNumber] = useState({
    id: '',
    number: '',
    notes: '',
    isBlocked: false,
  });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const url = window.location.href;
    if (url.indexOf('#blocked_phone_numbers') > -1) {
      containerRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const { data: blockedNumbersData, loading: blockedNumbersLoading } = useFindManyClinicBlockedPhoneNumberQuery({
    variables: { where: { clinicId: { equals: clinicId } } },
  });

  const [updateBlockedNumber, { loading: isUpdateBlockedPhoneNumberLoading }] =
    useUpdateOneClinicBlockedPhoneNumberMutation();

  const getMenuItems = useCallback(
    (id: string) => {
      const blockedNumber = blockedNumbersData?.findManyClinicBlockedPhoneNumber?.find(
        (blockedNumber) => blockedNumber.id === id,
      );
      const items: MenuItemProps[] = [
        {
          label: 'Edit',
          leftElement: <Icon size="sm" name="pen" />,
          value: 'duplicate',
          onSelect: (): void => {
            setEditPhoneNumber({
              id,
              number: formatPhoneNumber(blockedNumber?.phoneNumber),
              notes: blockedNumber?.notes || '',
              isBlocked: !blockedNumber?.unblockedBy?.id,
            });
            onEditBlockedNumberOpen();
          },
        },
      ];

      return items;
    },
    [blockedNumbersData?.findManyClinicBlockedPhoneNumber, onEditBlockedNumberOpen],
  );

  const handleEditBlockNumber = async (): Promise<void> => {
    try {
      const blockedPhoneNumber = blockedNumbersData?.findManyClinicBlockedPhoneNumber?.find(
        (blockedNumber) => blockedNumber.id === editPhoneNumber.id,
      );

      const blockedData: ClinicBlockedPhoneNumberUpdateInput = {};

      blockedData.notes = editPhoneNumber.notes;

      if (!editPhoneNumber.isBlocked && !blockedPhoneNumber?.unblockedBy) {
        blockedData.unblockedBy = { connect: { id: clinicUser?.id || '' } };
      }

      if (editPhoneNumber.isBlocked && blockedPhoneNumber?.unblockedBy) {
        blockedData.unblockedBy = { disconnect: true };
        blockedData.blockedBy = { connect: { id: clinicUser?.id || '' } };
        blockedData.createdAt = new Date().toISOString();
      }

      editPhoneNumber.isBlocked
        ? {
            unblockedBy: { connect: { id: clinicUser?.id || '' } },
          }
        : {
            unblockedBy: { disconnect: true },
            blockedBy: { connect: { id: clinicUser?.id || '' } },
          };
      await updateBlockedNumber({
        variables: {
          where: { id: editPhoneNumber.id },
          data: { ...blockedData },
        },
      });
    } catch (e) {
      console.log(e);
      toast({
        title: 'Error',
        description: 'Error while updating blocked number.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      onEditBlockedNumberClose();
    }
  };
  const openBlockNumberModal = (): void => {
    onBlockNumberOpen();
    trackEvent(SettingsSmsEvents.AddBlockedNumber);
  };
  return (
    <Flex flexDirection="column" paddingBottom="10" ref={containerRef} id="blocked_phone_numbers" maxWidth="800px">
      <Flex justifyContent="space-between">
        <Heading size="md">Blocked Phone Numbers</Heading>
        {hasAdminRoleAtCurrentClinic && (
          <Button
            variant="ghost"
            leftIcon={<Icon name="plus" />}
            isLoading={blockedNumbersLoading}
            onClick={openBlockNumberModal}
          >
            Block Number
          </Button>
        )}
      </Flex>
      {blockedNumbersData?.findManyClinicBlockedPhoneNumber?.length &&
      blockedNumbersData?.findManyClinicBlockedPhoneNumber?.length > 0 ? (
        <TableContainer padding="30px">
          <Table whiteSpace="normal">
            <Thead>
              <Tr>
                <Th>Phone Number</Th>
                <Th>Status</Th>
                <Th>Details</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {sortBy(blockedNumbersData?.findManyClinicBlockedPhoneNumber, (entry) => entry.createdAt)
                .reverse()
                .map((blockedNumber, index) => {
                  const blockedBy =
                    blockedNumber.blockedBy?.nameDisplay ||
                    `${blockedNumber.blockedBy?.firstName || ''} ${blockedNumber.blockedBy?.lastName || ''}`;
                  const unblockedBy =
                    blockedNumber.unblockedBy?.nameDisplay ||
                    `${blockedNumber.unblockedBy?.firstName || ''} ${blockedNumber.unblockedBy?.lastName || ''}`;
                  return (
                    <Tr key={blockedNumber.id} bgColor={index % 2 === 0 ? 'background.default' : 'background.subtle'}>
                      <Td minWidth="200px">{formatPhoneNumber(blockedNumber.phoneNumber)}</Td>
                      <Td>
                        {blockedNumber.unblockedBy ? (
                          <Tag variant="success" label="Unblocked" />
                        ) : (
                          <Tag variant="error" label="Blocked" />
                        )}
                      </Td>
                      <Td>
                        <Button
                          variant="ghost"
                          onClick={(): void => {
                            setBlockedNumberNotes({
                              number: formatPhoneNumber(blockedNumber.phoneNumber),
                              notes: blockedNumber.notes || '',
                              blockedBy: blockedBy.trim() || 'Support Team',
                              blockedAt: format(new Date(blockedNumber.createdAt), 'MM/dd/yy, h:mm a'),
                              unblockedBy,
                              unblockedAt: format(new Date(blockedNumber.updatedAt), 'MM/dd/yy, h:mm a'),
                            });
                            onViewNotesOpen();
                          }}
                        >
                          View
                        </Button>
                      </Td>
                      <Td>
                        <Flex justifyContent="flex-end">
                          {hasAdminRoleAtCurrentClinic && (
                            <Menu
                              buttonProps={{
                                variant: 'tertiary',
                                leftIconName: 'dotsVertical',
                                size: 'sm',
                                showRightIcon: false,
                              }}
                              listProps={{
                                canSelectAll: false,
                                canSelectGroups: false,
                                isMultiSelect: false,
                                isSearchable: false,
                                menuItems: getMenuItems(blockedNumber.id),
                              }}
                            />
                          )}
                        </Flex>
                      </Td>
                    </Tr>
                  );
                })}
            </Tbody>
          </Table>
        </TableContainer>
      ) : (
        <Flex justifyContent="center">
          <Text>No numbers in your blocked list</Text>
        </Flex>
      )}
      {/* Block number modal */}
      <BlockPhoneNumberModal isBlockNumberOpen={isBlockNumberOpen} onBlockNumberClose={onBlockNumberClose} />
      {/* View notes modal */}
      <Modal onClose={onViewNotesClose} isOpen={isViewNotesOpen} size="lg">
        <ModalHeader>{blockedNumberNotes.number}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={5}>
          {blockedNumberNotes.blockedBy.trim() && (
            <Text as="p" whiteSpace="break-spaces">
              {blockedNumberNotes.blockedAt} &nbsp; {blockedNumberNotes.blockedBy}{' '}
              <Text color="text.danger">blocked</Text> this number.
            </Text>
          )}
          {blockedNumberNotes.unblockedBy.trim() && (
            <Text as="p" whiteSpace="break-spaces">
              {blockedNumberNotes.unblockedAt} &nbsp; {blockedNumberNotes.unblockedBy}{' '}
              <Text color="text.success">unblocked</Text> this number.
            </Text>
          )}
          <Heading size="sm" mt="4">
            Notes
          </Heading>
          <Box maxHeight="50vh" overflowY="scroll">
            <Text as="p" whiteSpace="break-spaces" color={blockedNumberNotes.notes ? 'text.default' : 'text.subtle'}>
              {blockedNumberNotes.notes || 'No notes saved for this number.'}
            </Text>
          </Box>
        </ModalBody>
      </Modal>
      {/* Edit blocked number modal */}
      <Modal onClose={onEditBlockedNumberClose} isOpen={isEditBlockedNumberOpen} size="md">
        <ModalHeader>Edit Blocked Number</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={5}>
          <Text as="p" size="lg" fontWeight="bold">
            {editPhoneNumber.number}
          </Text>
          <Box mt="4" />
          <Textarea
            value={editPhoneNumber.notes}
            label="Notes"
            mb="4"
            onChange={(e): void => {
              setEditPhoneNumber((prevState) => {
                return { ...prevState, notes: e.target.value };
              });
            }}
          />

          <Switch
            size="lg"
            label="Block Number"
            labelPosition="start"
            isFieldInline
            isChecked={editPhoneNumber.isBlocked}
            onChange={(e): void => {
              setEditPhoneNumber((prevState) => {
                return { ...prevState, isBlocked: e.target.checked };
              });
            }}
          />
        </ModalBody>
        <ModalFooterButtons
          onCancel={onEditBlockedNumberClose}
          onSubmit={handleEditBlockNumber}
          isLoading={isUpdateBlockedPhoneNumberLoading}
        />
      </Modal>
    </Flex>
  );
};

export default BlockedPhoneNumbers;
